import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Form, Select, Button, Modal, Upload, Radio, message, Tag, Space, Popconfirm, Tooltip } from 'antd';
import { UploadOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { getExcelUploadsV2, createExcelUploadV2, previewExcelUploadV2, processExcelUploadV2, deleteExcelUploadV2, getRatesheetsV2, downloadExcelUploadV2 } from '../api';

const months = Array.from({ length: 12 }, (_, i) => ({ label: moment().month(i).format('MMMM'), value: i + 1 }));
const years = Array.from({ length: 8 }, (_, i) => ({ label: String(moment().year() - 3 + i), value: moment().year() - 3 + i }));

const ExcelUploadManager = () => {
  const navigate = useNavigate();
  const now = moment();
  const [form] = Form.useForm();
  const [listLoading, setListLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ month: now.month() + 1, year: now.year() });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [ratesheetSource, setRatesheetSource] = useState('new');
  const [ratesheetOptions, setRatesheetOptions] = useState([]);
  const [ratesheetFile, setRatesheetFile] = useState(null);
  const [timesheetFile, setTimesheetFile] = useState(null);
  const monthYearLabel = useMemo(() => `${filters.year}-${String(filters.month).padStart(2, '0')}`, [filters]);

  const fetchList = async (params = {}) => {
    setListLoading(true);
    const res = await getExcelUploadsV2({ month: filters.month, year: filters.year, ...params });
    setItems(res.data || []);
    setListLoading(false);
  };

  useEffect(() => { fetchList(); }, [filters.month, filters.year]);

  const openUpload = (preset) => {
    const m = preset?.month ?? filters.month;
    const y = preset?.year ?? filters.year;
    form.setFieldsValue({ month: m, year: y, ratesheet_source: 'new', previous_ratesheet_id: null });
    setRatesheetSource('new');
    setRatesheetFile(null);
    setTimesheetFile(null);
    setUploadOpen(true);
    fetchRatesheets(m, y);
  };

  const fetchRatesheets = async (month, year) => {
    const res = await getRatesheetsV2(month, year);
    setRatesheetOptions((res.data || []).map(r => ({ label: `${r.ratesheet_original_filename || ('Ratesheet #' + r.id)} (${String(r.month).padStart(2, '0')}-${r.year})`, value: r.id })));
  };

  const showMissingCodesModal = (res) => {
    const codes = res.data?.missing_in_ratesheet || res.missing_in_ratesheet || [];
    setTimeout(() => {
      Modal.error({
        title: res.message || 'Processing Error',
        content: (
          <div>
            <p>The following employee codes from the timesheet were not found in the ratesheet:</p>
            <ul style={{ maxHeight: '250px', overflowY: 'auto', paddingLeft: '20px' }}>
              {codes.map(code => (
                <li key={code} style={{ marginBottom: '4px' }}><strong>{code}</strong></li>
              ))}
            </ul>
            <p>Please update the ratesheet with these codes and try again.</p>
          </div>
        ),
        width: 500,
      });
    }, 300);
  };

  const showNewEmployeesModal = (newEmps) => {
    Modal.info({
      title: 'New Employees Detected',
      content: (
        <div>
          <p>The following employees were not in the master list and have been automatically added:</p>
          <ul style={{ maxHeight: '250px', overflowY: 'auto', paddingLeft: '20px' }}>
            {newEmps.map(emp => (
              <li key={emp.empcode}><strong>{emp.empcode}</strong> - {emp.name}</li>
            ))}
          </ul>
          <p>You can now download their payslips in the Payroll Report.</p>
        </div>
      ),
      width: 500,
    });
  };

  const onUploadSubmit = async () => {
    try {
      const vals = await form.validateFields();
      if (ratesheetSource === 'new' && !ratesheetFile && !timesheetFile) {
        message.error('Choose files');
        return;
      }
      setUploadLoading(true);
      const fd = new FormData();
      fd.append('month', vals.month);
      fd.append('year', vals.year);
      fd.append('ratesheet_source', ratesheetSource);
      if (ratesheetSource === 'previous' && vals.previous_ratesheet_id) fd.append('previous_ratesheet_id', vals.previous_ratesheet_id);
      if (ratesheetSource === 'new' && ratesheetFile) fd.append('ratesheet', ratesheetFile);
      if (timesheetFile) fd.append('timesheet', timesheetFile);
      const res = await createExcelUploadV2(fd);
      if (res.success) {
        message.success('Uploaded');
        setUploadOpen(false);
        fetchList();
      } else {
        if (res.data && res.data.missing_in_ratesheet) {
          setUploadOpen(false);
          showMissingCodesModal(res);
        } else {
          message.error(res.message || 'Failed');
        }
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const onPreview = async (id, file) => {
    const res = await previewExcelUploadV2(id, file);
    const html = typeof res.data === 'string' ? res.data : res.data;
    const w = window.open();
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  const onProcess = async (id) => {
    const res = await processExcelUploadV2(id);
    if (res.success) {
      if (res.data?.newly_added_employees?.length > 0) {
        showNewEmployeesModal(res.data.newly_added_employees);
      } else {
        message.success('Processed');
      }
      fetchList();
    } else {
      if (res.data && res.data.missing_in_ratesheet) {
        showMissingCodesModal(res);
      } else {
        message.error(res.message || 'Failed');
      }
    }
  };

  const onDelete = async (id) => {
    const res = await deleteExcelUploadV2(id);
    if (res.success) {
      message.success('Deleted');
      fetchList();
    } else {
      message.error(res.message || 'Failed');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: 'Type', dataIndex: 'type', width: 120, render: t => <Tag color={t === 'ratesheet' ? 'blue' : 'green'}>{t}</Tag> },
    { title: 'Month', dataIndex: 'month', width: 100 },
    { title: 'Year', dataIndex: 'year', width: 100 },
    {
      title: 'Ratesheet', dataIndex: 'ratesheet_original_filename', render: (_, r) => (
        <Space size={4}>
          <span>{r.ratesheet_original_filename || '-'}</span>
          {r.ratesheet_file_path && (
            <>
              <Tooltip title="Preview">
                <EyeOutlined onClick={() => onPreview(r.id, 'ratesheet')} style={{ cursor: 'pointer' }} />
              </Tooltip>
              <Tooltip title="Download">
                <DownloadOutlined onClick={() => downloadExcelUploadV2(r.id, 'ratesheet')} style={{ cursor: 'pointer' }} />
              </Tooltip>
            </>
          )}
        </Space>
      )
    },
    {
      title: 'Timesheet', dataIndex: 'timesheet_original_filename', render: (_, r) => (
        <Space size={4}>
          <span>{r.timesheet_original_filename || '-'}</span>
          {r.timesheet_file_path && (
            <>
              <Tooltip title="Preview">
                <EyeOutlined onClick={() => onPreview(r.id, 'timesheet')} style={{ cursor: 'pointer' }} />
              </Tooltip>
              <Tooltip title="Download">
                <DownloadOutlined onClick={() => downloadExcelUploadV2(r.id, 'timesheet')} style={{ cursor: 'pointer' }} />
              </Tooltip>
            </>
          )}
        </Space>
      )
    },
    { title: 'Rows', dataIndex: ['details', 'rows'], width: 100, render: (_, r) => (r.details && Array.isArray(r.details.rows)) ? r.details.rows.length : null },
    { title: 'Status', dataIndex: 'status', width: 120, render: s => <Tag color={s === 'processed' ? 'green' : 'gold'}>{s}</Tag> },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 180, render: (_, r) => (
        <Space size="small">
          <Button size="small" type="primary" onClick={() => onProcess(r.id)}>Process Timesheet</Button>
          <Popconfirm title="Delete?" onConfirm={() => onDelete(r.id)}><Button size="small" danger>Delete</Button></Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <Card title="Excel Uploads" extra={<Space><Button icon={<UploadOutlined />} onClick={() => openUpload()}>Upload</Button><Button onClick={() => navigate('/report/processing')}>Report</Button></Space>}>
        <Form layout="inline" style={{ marginBottom: 12 }}>
          <Form.Item label="Month">
            <Select style={{ width: 160 }} value={filters.month} options={months} onChange={(v) => setFilters({ ...filters, month: v })} />
          </Form.Item>
          <Form.Item label="Year">
            <Select style={{ width: 120 }} value={filters.year} options={years} onChange={(v) => setFilters({ ...filters, year: v })} />
          </Form.Item>
          <Form.Item>
            <Button onClick={() => fetchList()} loading={listLoading}>Refresh</Button>
          </Form.Item>
        </Form>
        <Table rowKey="id" loading={listLoading} dataSource={items} columns={columns} size="small" scroll={{ x: 900 }} />
      </Card>

      <Modal open={uploadOpen} title="Upload Excel" onCancel={() => setUploadOpen(false)} onOk={onUploadSubmit} confirmLoading={uploadLoading}>
        <Form form={form} layout="vertical" initialValues={{ month: filters.month, year: filters.year, ratesheet_source: 'new' }}>
          <Form.Item name="month" label="Month" rules={[{ required: true }]}>
            <Select options={months} />
          </Form.Item>
          <Form.Item name="year" label="Year" rules={[{ required: true }]}>
            <Select options={years} />
          </Form.Item>
          <Form.Item label="Ratesheet Source">
            <Radio.Group value={ratesheetSource} onChange={(e) => { setRatesheetSource(e.target.value); const v = form.getFieldsValue(); fetchRatesheets(v.month, v.year); }}>
              <Radio value="new">Upload New</Radio>
              <Radio value="previous">Use Previous</Radio>
            </Radio.Group>
          </Form.Item>
          {ratesheetSource === 'previous' ? (
            <Form.Item name="previous_ratesheet_id" label="Previous Ratesheet" rules={[{ required: true }]}>
              <Select options={ratesheetOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          ) : (
            <Form.Item label="Ratesheet">
              <Upload beforeUpload={file => { setRatesheetFile(file); return false; }} showUploadList={ratesheetFile ? [{ name: ratesheetFile.name }] : false} maxCount={1} accept=".xlsx,.xls,.csv">
                <Button icon={<UploadOutlined />}>Choose File</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item label="Timesheet">
            <Upload beforeUpload={file => { setTimesheetFile(file); return false; }} showUploadList={timesheetFile ? [{ name: timesheetFile.name }] : false} maxCount={1} accept=".xlsx,.xls,.csv">
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </Form.Item>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
            Note: Employees with matching <strong>Employee Codes</strong> or <strong>Names</strong> will be updated; others will be added as new entries.
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ExcelUploadManager;